import { fetchInstallerStatus, isCachedInstalled, markInstalled } from '@root/services/installerStatus';

/* global LOGIN_PAGE_URL */

/**
 * Bootstrap redirect (Plan 003 §7 Phase 1 — first-boot install wizard).
 *
 * `@apexl/icarus-core/src/router/index.js` ships a single catch-all route
 * and no `beforeEach` hook or dedicated plugin seam for adding one — but it
 * does export the real `vue-router` instance, and the project's own
 * `index.js` already owns wiring that instance into the app (`app.use(router)`).
 * Registering the guard there, on the exported router, before it's
 * installed, is the least-invasive extension point the framework actually
 * offers: the same "project owns its own entry point" seam the component
 * override / theme-tier system in `index.js` already relies on, rather than
 * forking icarus-core's router or hacking `Icarus.vue`'s page-fetch cycle.
 *
 * Behaviour:
 *   - Uninstalled instance (any `needs-*` state): every route, including
 *     `/login`, redirects to `/install`.
 *   - Installed instance (`installer/status` 404s): `/install` itself
 *     redirects to the login route; every other route proceeds normally.
 *   - The "installed" answer is cached in `localStorage` the first time it's
 *     observed, so an installed instance's normal logged-in navigation never
 *     repeats the status call — this must add no latency/requests once a
 *     site is live.
 */
function installInstallerGuard(router) {
  router.beforeEach(async (to) => {
    if (isCachedInstalled()) {
      return to.path === '/install' ? `/${LOGIN_PAGE_URL}` : true;
    }

    let status;
    try {
      status = await fetchInstallerStatus();
    } catch (error) {
      // Network hiccup / backend unreachable — fail open. A guard that can
      // never resolve would strand every route in the SPA, which is worse
      // than occasionally skipping the install-state check.
      // eslint-disable-next-line no-console
      console.error('[installer-guard] status check failed', error);
      return true;
    }

    if (status.installed) {
      markInstalled();
      return to.path === '/install' ? `/${LOGIN_PAGE_URL}` : true;
    }

    return to.path === '/install' ? true : '/install';
  });
}

export default installInstallerGuard;
